/**
 * What to say about a failed request — the shared half, once rather than once per screen.
 *
 * A screen still supplies the two sentences that are genuinely about *its* subject ("Video not found"
 * is the wrong sentence about a feed); those live in that screen's own section of the catalogue. What
 * is here is everything true regardless of what was being fetched.
 *
 * The entries are whole {@link ProblemCopy} values, `retryable` included, and that is deliberate: the
 * flag is not a separate decision from the words but the same one — `ProblemPanel` reads it to decide
 * whether a Try again button appears, and a sentence that ends "it usually clears on its own" with no
 * button under it is a different sentence. A second catalogue inherits both by copying the structure.
 *
 * **No status codes, endpoints or header names.** Every one of them is a fact about how Visious and
 * Invidious talk to each other, and there is nothing a reader can do with any of it. What a reader can
 * act on is three things — is this mine to fix, does signing in help, is trying again worth it — and
 * every sentence here answers those and stops. The classification that picks the sentence still reads
 * the whole response.
 */
export const problem = {
  /**
   * The five meanings of one refusal, in words. Only the response body separates them, which
   * is the whole reason `classifyAuthFailure` exists; what reaches the reader is which of five *next
   * steps* is the right one, which is the only part that differs for them.
   *
   * These live in the shared section rather than on the sign-in page because **every** authenticated
   * screen hits them, and a browser showed what happens without that: a session the instance would not
   * accept reported "you are signed out" to a reader who *was* signed in. And `invalid-scope` is the one
   * where the obvious next step (refresh the session) provably cannot work (`decideAuthRecovery`) — so
   * its sentence asks for the whole permission list instead.
   */
  auth: {
    notAuthenticated: {
      title: 'You are signed out',
      detail: 'This page needs a session on your instance, and there is none stored in this browser.',
      retryable: false,
    },
    tokenExpired: {
      title: 'Your session has expired',
      detail:
        'Sessions run out after a while. Signing in again is all it takes, and nothing about your account changes.',
      retryable: false,
    },
    invalidSignature: {
      title: 'This session is not valid here',
      detail:
        'It belongs to a different instance, or it has been altered. Accounts are per instance, so signing in again is what replaces it.',
      retryable: false,
    },
    invalidScope: {
      title: 'This session was not given permission for that',
      detail:
        'The sign-in itself is fine. It simply does not cover this. Signing in again and approving the whole list is the fix, since refreshing the same one gives back the same gap.',
      retryable: false,
    },
    malformedToken: {
      title: 'The stored session cannot be read',
      detail: 'What is saved in this browser is not something Visious can use. Signing in again replaces it.',
      retryable: false,
    },
    unrecognised: {
      title: 'The instance refused this',
      detail: 'It did not say why, so signing in again is the only thing worth trying.',
      retryable: false,
    },
  },

  /** A call this app did not make at all, because the session provably does not cover it. */
  outOfScope: {
    title: 'This session cannot do that',
    detail:
      'It was not given permission for this, so Visious did not ask. Signing in again asks for the full set.',
    retryable: false,
  },

  /** One entry per `ApiErrorKind` core can classify. Missing one is a type error in `api/problem.ts`. */
  kind: {
    endpointDisabled: {
      title: 'This instance has switched that off',
      detail: 'Its administrator turned this off, so there is nothing to show and nothing to retry.',
      retryable: false,
    },
    instanceWarming: {
      title: 'The instance is busy',
      detail: 'It is under load right now. This usually clears on its own within a few seconds.',
      retryable: true,
    },
    rateLimited: {
      title: 'Too many requests',
      detail: 'The instance is asking for fewer requests. Give it a moment and try again.',
      retryable: true,
    },
    network: {
      title: 'Could not reach the instance',
      detail: 'Nothing answered at all. Check your connection, or the instance may be down.',
      retryable: true,
    },
    aborted: { title: 'Cancelled', detail: 'This was cancelled before it finished.', retryable: true },
    upstreamBroken: {
      title: 'YouTube would not serve this',
      detail:
        'Your instance asked and got an error back instead of an answer. Trying again gets the same result.',
      retryable: false,
    },
    /**
     * The kind is named for a signed media URL past its expiry, and this is every *other* place the
     * refusal lands — where no media was being fetched, so that reading is simply wrong. What is left is
     * a refusal with no reason attached, from the instance or from whatever sits in front of it.
     */
    urlsExpired: {
      title: 'The instance refused this request',
      detail:
        'It would not answer and did not say why. Something in front of it may be turning the request away.',
      retryable: false,
    },
    server: {
      title: 'The instance failed on this',
      detail:
        'It broke on this request, and Visious had already retried it. The instance is unwell, and this is nothing to do with what you asked for.',
      retryable: true,
    },
    client: {
      title: 'The instance rejected this request',
      detail:
        'It understood what was asked and refused it, which means Visious asked for the wrong thing. That is a bug here, not anything you did.',
      retryable: false,
    },
    malformedResponse: {
      title: 'Could not read the answer',
      detail:
        'The instance replied with something Visious could not make sense of. Nothing about the request was wrong, so the next attempt may well land.',
      retryable: true,
    },
  },

  /** §3's instance address is the one thing behind this a reader owns, so the sentence points at it. */
  badUrl: {
    title: 'The instance address will not work',
    detail: 'Visious could not build a usable address from it. Check the instance under Settings.',
    retryable: false,
  },

  malformedJson: {
    title: 'Something else answered',
    detail:
      'The reply did not come from your instance. It is usually a proxy or a captive portal on the connection you are using.',
    retryable: true,
  },

  emptyResponse: {
    title: 'The instance answered with nothing',
    detail:
      'It replied, but with no content at all. Some videos do this every time, and others work on the next attempt.',
    retryable: true,
  },
} as const;
